import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, FACEBOOK_APP_ID, GOOGLE_CLIENT_ID } from "../../environment";
import { loginUser } from "../../core/data/redux/action";
import { all_routes } from "../router/all_routes";

type AuthMode = "login" | "signup";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

declare global {
  interface Window {
    google?: any;
    FB?: any;
    fbAsyncInit?: () => void;
  }
}

const AuthModal = ({ isOpen, onClose }: AuthModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routes = all_routes;
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<AuthMode>("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const resetMessages = () => {
    setMessage("");
    setErrorMessage("");
  };

  const closeModal = () => {
    resetMessages();
    onClose();
  };

  const handleLogin = async (loginEmail = email, loginPassword = password) => {
    const response = await axios.post(`${API_BASE_URL}/auth/login`, {
      email: loginEmail,
      password: loginPassword
    });

    dispatch(loginUser(response.data));
    closeModal();
    navigate(routes.dashboard);
  };

  const handleGoogleLogin = async (credential: string) => {
    resetMessages();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/google`, {
        credential
      });

      dispatch(loginUser(response.data));
      closeModal();
      navigate(routes.dashboard);
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || "Google sign-in failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFacebookLogin = () => {
    resetMessages();

    if (!FACEBOOK_APP_ID) {
      setErrorMessage("Facebook login is not configured yet.");
      return;
    }

    if (!window.FB) {
      setErrorMessage("Facebook login is still loading. Please try again in a moment.");
      return;
    }

    setIsLoading(true);
    const completeFacebookLogin = async (loginResponse: any) => {
      try {
        if (!loginResponse.authResponse) {
          setErrorMessage("Facebook sign-in was cancelled.");
          return;
        }

        const response = await axios.post(`${API_BASE_URL}/auth/facebook`, {
          accessToken: loginResponse.authResponse.accessToken,
          userID: loginResponse.authResponse.userID
        });

        dispatch(loginUser(response.data));
        closeModal();
        navigate(routes.dashboard);
      } catch (error: any) {
        setErrorMessage(error?.response?.data?.message || "Facebook sign-in failed. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    window.FB.login(
      (loginResponse: any) => {
        completeFacebookLogin(loginResponse);
      },
      { scope: "public_profile,email", return_scopes: true }
    );
  };

  useEffect(() => {
    if (!isOpen || !GOOGLE_CLIENT_ID) {
      return;
    }

    const renderGoogleButton = () => {
      if (!window.google || !googleButtonRef.current) {
        return;
      }

      googleButtonRef.current.innerHTML = "";
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response: { credential?: string }) => {
          if (response.credential) {
            handleGoogleLogin(response.credential);
          } else {
            setErrorMessage("Google did not return a sign-in credential.");
          }
        },
        ux_mode: "popup"
      });
      const googleButtonWidth = Math.floor(googleButtonRef.current.getBoundingClientRect().width || 220);

      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: "outline",
        size: "large",
        text: mode === "login" ? "signin_with" : "signup_with",
        shape: "rectangular",
        width: googleButtonWidth
      });
    };

    if (window.google) {
      renderGoogleButton();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>("script[src='https://accounts.google.com/gsi/client']");

    if (existingScript) {
      existingScript.addEventListener("load", renderGoogleButton);
      return () => existingScript.removeEventListener("load", renderGoogleButton);
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = renderGoogleButton;
    script.onerror = () => setErrorMessage("Google sign-in could not be loaded. Please try email sign-in.");
    document.body.appendChild(script);
  }, [isOpen, mode]);

  useEffect(() => {
    if (!isOpen || !FACEBOOK_APP_ID) {
      return;
    }

    const initializeFacebook = () => {
      if (!window.FB) {
        return;
      }

      window.FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: false,
        version: "v20.0"
      });
    };

    if (window.FB) {
      initializeFacebook();
      return;
    }

    window.fbAsyncInit = initializeFacebook;

    if (document.getElementById("facebook-jssdk")) {
      return;
    }

    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.onerror = () => setErrorMessage("Facebook sign-in could not be loaded. Please try email sign-in.");
    document.body.appendChild(script);
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    resetMessages();

    if (!email || !password || (mode === "signup" && !name)) {
      setErrorMessage(mode === "signup" ? "Name, email, and password are required." : "Email and password are required.");
      return;
    }

    setIsLoading(true);
    try {
      if (mode === "signup") {
        await axios.post(`${API_BASE_URL}/users/add`, {
          name,
          email,
          password
        });
        setMessage("Account created. Signing you in...");
      }

      await handleLogin(email, password);
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || "We could not complete your request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="modal auth-popup-modal fade d-block show" role="dialog" aria-modal="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div className="auth-title-block">
                <span className="auth-brand-mark">
                  <i className="fa-solid fa-car" />
                </span>
                <h4 className="modal-title">{mode === "login" ? "Sign In" : "Create Account"}</h4>
                <p>{mode === "login" ? "Access your bookings and documents." : "Create your client account with email."}</p>
              </div>
              <button type="button" className="close-btn" onClick={closeModal} aria-label="Close">
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              {message && <div className="alert alert-success">{message}</div>}
              {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

              <div className="auth-social-grid">
                <div className="auth-google-btn" ref={googleButtonRef} />
                <button type="button" className="auth-social-btn auth-facebook-btn" onClick={handleFacebookLogin} disabled={isLoading}>
                  <i className="fab fa-facebook-f" /> Facebook
                </button>
              </div>

              <div className="auth-divider"><span>or use email</span></div>

              <form onSubmit={handleSubmit}>
                {mode === "signup" && (
                  <div className="input-block">
                    <label className="form-label" htmlFor="auth-name">Name</label>
                    <input
                      id="auth-name"
                      name="name"
                      type="text"
                      className="form-control"
                      placeholder="Enter your name"
                      autoComplete="name"
                      value={name}
                      onChange={event => setName(event.target.value)}
                    />
                  </div>
                )}
                <div className="input-block">
                  <label className="form-label" htmlFor="auth-email">Email</label>
                  <input
                    id="auth-email"
                    name="email"
                    type="email"
                    className="form-control"
                    placeholder="Enter email"
                    autoComplete="email"
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                  />
                </div>
                <div className="input-block">
                  <label className="form-label" htmlFor="auth-password">Password</label>
                  <input
                    id="auth-password"
                    name="password"
                    type="password"
                    className="form-control"
                    placeholder="Enter password"
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                    value={password}
                    onChange={event => setPassword(event.target.value)}
                  />
                </div>
                <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
                  {isLoading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
                </button>
              </form>

              <div className="auth-mode-switch">
                {mode === "login" ? (
                  <button type="button" onClick={() => { resetMessages(); setMode("signup"); }}>
                    Create an account
                  </button>
                ) : (
                  <button type="button" onClick={() => { resetMessages(); setMode("login"); }}>
                    Already have an account? Sign in
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={closeModal} />
    </>
  );
};

export default AuthModal;
